import { openDB, IDBPDatabase } from 'idb';
import { QuestionAnswerState, UIPreferences, ExecutionResult, NotebookCell } from '../types';

const DB_NAME = 'UniboMLExamsDB';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('answers')) {
          db.createObjectStore('answers', { keyPath: 'questionId' });
        }
        if (!db.objectStoreNames.contains('outputs')) {
          db.createObjectStore('outputs', { keyPath: 'questionId' });
        }
      },
    });
  }
  return dbPromise;
}

const PREFS_KEY = 'unibo_ml_preferences_v1';

const DEFAULT_PREFERENCES: UIPreferences = {
  theme: 'dark',
  fontSize: 14,
  lastQuestionId: 'clustering-01',
  focusMode: false,
  sidebarCollapsed: false,
  sidebarWidth: 320,
  promptPercent: 50,
  consoleHeight: 288,
  consoleCollapsed: false,
  activeOutputTab: 'console',
  editorMode: 'notebook',
  timerMode: 'stopwatch',
  timerDurationMinutes: 90,
};

/** Strip Vue proxies / runtime flags into IndexedDB-safe plain objects. */
export function serializeNotebookCells(cells: NotebookCell[] | undefined | null): NotebookCell[] {
  if (!cells?.length) return [];
  return cells.map((cell, index) => ({
    id: cell.id || `cell_${Date.now()}_${index}`,
    type: cell.type === 'markdown' ? 'markdown' : 'code',
    code: typeof cell.code === 'string' ? cell.code : '',
    executionCount: cell.executionCount,
    // Persist lightweight output text, but drop huge base64 plots from autosave
    output: cell.output
      ? {
          stdout: cell.output.stdout || '',
          stderr: cell.output.stderr || '',
          error: cell.output.error,
          plots: Array.isArray(cell.output.plots) ? cell.output.plots.slice(0, 3) : [],
          executionTimeMs: cell.output.executionTimeMs || 0,
          timestamp: cell.output.timestamp || new Date().toISOString(),
        }
      : undefined,
  }));
}

function toPlainAnswerState(state: QuestionAnswerState): QuestionAnswerState {
  return JSON.parse(JSON.stringify({
    questionId: state.questionId,
    code: state.code || '',
    cells: serializeNotebookCells(state.cells),
    status: state.status || 'not_started',
    checklistChecked: Array.isArray(state.checklistChecked) ? [...state.checklistChecked] : [],
    updatedAt: state.updatedAt || new Date().toISOString(),
    snapshots: Array.isArray(state.snapshots)
      ? state.snapshots.map((s) => ({
          id: s.id,
          timestamp: s.timestamp,
          code: s.code || '',
          note: s.note,
        }))
      : [],
  }));
}

export async function getAnswerState(questionId: string): Promise<QuestionAnswerState | undefined> {
  const db = await getDB();
  const state = await db.get('answers', questionId);
  if (!state) return undefined;
  return {
    ...state,
    cells: serializeNotebookCells(state.cells),
    code: state.code || '',
    checklistChecked: state.checklistChecked || [],
    snapshots: state.snapshots || [],
  };
}

export async function getAllAnswerStates(): Promise<Record<string, QuestionAnswerState>> {
  const db = await getDB();
  const all = await db.getAll('answers');
  const result: Record<string, QuestionAnswerState> = {};
  for (const item of all) {
    result[item.questionId] = {
      ...item,
      cells: serializeNotebookCells(item.cells),
      code: item.code || '',
      checklistChecked: item.checklistChecked || [],
      snapshots: item.snapshots || [],
    };
  }
  return result;
}

export async function saveAnswerState(state: QuestionAnswerState): Promise<void> {
  const db = await getDB();
  const plain = toPlainAnswerState({
    ...state,
    updatedAt: new Date().toISOString(),
  });
  await db.put('answers', plain);
}

export async function addSnapshot(
  questionId: string,
  code: string,
  note: string = 'Autosave',
  cells?: NotebookCell[]
): Promise<QuestionAnswerState> {
  const existing = await getAnswerState(questionId) || {
    questionId,
    code,
    cells: [],
    status: 'in_progress' as const,
    checklistChecked: [],
    updatedAt: new Date().toISOString(),
    snapshots: [],
  };

  const newSnapshot = {
    id: `snap_${Date.now()}`,
    timestamp: new Date().toISOString(),
    code,
    note,
  };

  const snapshots = [newSnapshot, ...(existing.snapshots || [])].slice(0, 15);
  const updatedState: QuestionAnswerState = {
    ...existing,
    code,
    // Keep notebook cells in sync when snapshots are written
    cells: cells !== undefined ? serializeNotebookCells(cells) : serializeNotebookCells(existing.cells),
    updatedAt: new Date().toISOString(),
    snapshots,
  };

  await saveAnswerState(updatedState);
  return updatedState;
}

export async function saveExecutionResult(questionId: string, result: ExecutionResult): Promise<void> {
  const db = await getDB();
  await db.put('outputs', {
    questionId,
    ...result,
  });
}

export async function getExecutionResult(questionId: string): Promise<ExecutionResult | undefined> {
  const db = await getDB();
  return db.get('outputs', questionId);
}

export function getPreferences(): UIPreferences {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFERENCES };
    const parsed = JSON.parse(raw);
    const merged: UIPreferences = { ...DEFAULT_PREFERENCES, ...parsed };
    // Migrate legacy focusMode → sidebarCollapsed
    if (typeof parsed.sidebarCollapsed !== 'boolean' && typeof parsed.focusMode === 'boolean') {
      merged.sidebarCollapsed = parsed.focusMode;
    }
    return merged;
  } catch (e) {
    return { ...DEFAULT_PREFERENCES };
  }
}

export function savePreferences(prefs: Partial<UIPreferences>): UIPreferences {
  const current = getPreferences();
  const updated = { ...current, ...prefs };
  localStorage.setItem(PREFS_KEY, JSON.stringify(updated));
  return updated;
}

export async function exportAllProgressJSON(): Promise<string> {
  const answers = await getAllAnswerStates();
  const preferences = getPreferences();
  const payload = {
    app: 'UNIBO_ML_EXAM_PRACTICE',
    version: 1,
    exportedAt: new Date().toISOString(),
    preferences,
    answers,
  };
  return JSON.stringify(payload, null, 2);
}

export async function importProgressJSON(jsonData: string): Promise<boolean> {
  try {
    const parsed = JSON.parse(jsonData);
    if (!parsed || parsed.app !== 'UNIBO_ML_EXAM_PRACTICE') {
      throw new Error('Invalid export file format.');
    }

    if (parsed.preferences) {
      savePreferences(parsed.preferences);
    }

    if (parsed.answers) {
      const db = await getDB();
      const tx = db.transaction('answers', 'readwrite');
      for (const qId of Object.keys(parsed.answers)) {
        await tx.store.put(parsed.answers[qId]);
      }
      await tx.done;
    }
    return true;
  } catch (e) {
    console.error('Failed to import progress:', e);
    return false;
  }
}

export async function resetAllData(): Promise<void> {
  const db = await getDB();
  await db.clear('answers');
  await db.clear('outputs');
  localStorage.removeItem(PREFS_KEY);
}
